
import { printf, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { comparePrimitives, compare, compareArrays, safeHash, equals } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { ofSeq, map as map_1, cons, tail, head, isEmpty, concat, ofArray, indexed, contains, filter, collect, tryFindIndex, empty, singleton } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { FSharpMap__ContainsKey, filter as filter_1, toList, empty as empty_2, FSharpMap__Add, FSharpMap__TryFind, map, tryPick } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { value, some } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { SimGraph_values, SimGraph_tryFind } from "./SimGraphTypes.js";
import { isCustom, getCustomComponentType, isInput, getCustomName } from "../Common/Helpers.js";
import { empty as empty_1, FSharpSet__Add, FSharpSet__Contains } from "../fable_modules/fable-library-js.5.13.0/Set.js";
import { filter as filter_2, map as map_2 } from "../fable_modules/fable-library-js.5.13.0/Seq.js";

/**
 * Tells wether a component is clocked or not. Note that Custom components may
 * be clocked (cannot tell without recursively analysing them), so they are
 * considered synchronous.
 */
export function couldBeSynchronousComponent(compType) {
    switch (compType.tag) {
        case 31:
        case 32:
        case 33:
        case 34:
        case 35:
        case 37:
        case 38:
        case 36:
        case 40:
        case 41:
        case 42:
        case 26:
            return true;
        case 0:
        case 1:
        case 3:
        case 4:
        case 7:
        case 6:
        case 47:
        case 5:
        case 27:
        case 29:
        case 30:
        case 28:
        case 8:
        case 46:
        case 10:
        case 11:
        case 12:
        case 13:
        case 14:
        case 15:
        case 16:
        case 17:
        case 18:
        case 19:
        case 20:
        case 24:
        case 21:
        case 23:
        case 25:
        case 22:
        case 9:
        case 39:
        case 2:
        case 50:
        case 51:
        case 52:
        case 53:
            return false;
        case 54:
        case 55:
            return false;
        default:
            return toFail(`Legacy components ${compType} should never be read!`);
    }
}

/**
 * used to do asynchronous cycle checking on atomic components with non-trivial asynch paths.
 * should this, or something like it, also be used for in dependency cycle checking?
 * returns Some (async outputPortNumbers from inputPortNumber) if component is hybrid, otherwise None.
 */
export function getHybridComponentAsyncOuts(compType, inputPortNumber) {
    if (compType.tag === 42) {
        if (equals(inputPortNumber, 0)) {
            return singleton(0);
        }
        else {
            return empty();
        }
    }
    else {
        return undefined;
    }
}

export function isHybridComponent(compType) {
    return getHybridComponentAsyncOuts(compType, 0) != null;
}

/**
 * Find out whether a simulation graph has some synchronous components.
 */
export function hasSynchronousComponents(graph) {
    const _arg = tryPick((compId_1, isSync) => {
        if (isSync) {
            return some(undefined);
        }
        else {
            return undefined;
        }
    }, map((compId, comp) => {
        const matchValue = comp.Type;
        switch (matchValue.tag) {
            case 31:
            case 32:
            case 33:
            case 34:
            case 35:
            case 37:
            case 38:
            case 36:
            case 40:
            case 41:
            case 42:
                return true;
            case 26:
                return hasSynchronousComponents(value(comp.CustomSimulationGraph));
            case 0:
            case 1:
            case 3:
            case 4:
            case 6:
            case 47:
            case 5:
            case 27:
            case 29:
            case 28:
            case 30:
            case 8:
            case 46:
            case 10:
            case 11:
            case 12:
            case 13:
            case 14:
            case 15:
            case 16:
            case 17:
            case 18:
            case 19:
            case 20:
            case 25:
            case 21:
            case 24:
            case 23:
            case 22:
            case 9:
            case 39:
            case 7:
            case 2:
            case 50:
            case 51:
            case 52:
            case 53:
                return false;
            case 54:
            case 55:
                return false;
            default:
                return toFail(`legacy components should never be read ${comp.Type}`);
        }
    }, graph));
    if (_arg == null) {
        return false;
    }
    else {
        return true;
    }
}

export function getNodeOrFail(graph, id) {
    const matchValue = SimGraph_tryFind(id, graph);
    if (matchValue != null) {
        return matchValue;
    }
    else {
        return toFail(printf("what? getNodeOrFail received invalid component id: %A"))(id);
    }
}

function labelToPortNumber(label, labels) {
    const matchValue = tryFindIndex((y) => (label === y), labels);
    if (matchValue != null) {
        return matchValue | 0;
    }
    else {
        return toFail(printf("what? Label %s not present in %A"))(label)(labels) | 0;
    }
}

function getCustomCombinatorialOutputs(combRoutes, customNode, inputPortNumber) {
    let combOutputs;
    const matchValue = FSharpMap__TryFind(combRoutes, getCustomName(customNode.Type));
    if (matchValue != null) {
        const matchValue_1 = FSharpMap__TryFind(matchValue, inputPortNumber);
        combOutputs = ((matchValue_1 != null) ? matchValue_1 : toFail(printf("what? getCustomCombinatorialOutputs 2")));
    }
    else {
        combOutputs = toFail(printf("what? getCustomCombinatorialOutputs 1"));
    }
    return collect((tuple) => tuple[1], filter((tupledArg) => contains(tupledArg[0], combOutputs, {
        Equals: equals,
        GetHashCode: (x) => (safeHash(x) | 0),
    }), indexed(ofArray(customNode.Outputs))));
}

/**
 * Given a map of combinatorial routes from inputs to outputs for every
 * simulation graph, perform a lookup to find the combinatorial routes from a
 * given input to the outputs. Then filter the outputs of the custom node to
 * only point to the combinatorial children (i.e. the ones connected to the
 * combinatorial outptus).
 */
export function getCombinatorialOutputs(combRoutes, node, inputPortNumberOpt) {
    const matchValue = node.Type;
    let matchResult, comp_2, comp_3;
    switch (matchValue.tag) {
        case 26: {
            matchResult = 0;
            break;
        }
        case 42: {
            if (equals(inputPortNumberOpt, 0)) {
                matchResult = 1;
            }
            else if (couldBeSynchronousComponent(matchValue)) {
                matchResult = 2;
                comp_2 = matchValue;
            }
            else {
                matchResult = 3;
                comp_3 = matchValue;
            }
            break;
        }
        default:
            if (couldBeSynchronousComponent(matchValue)) {
                matchResult = 2;
                comp_2 = matchValue;
            }
            else {
                matchResult = 3;
                comp_3 = matchValue;
            }
    }
    switch (matchResult) {
        case 0:
            return getCustomCombinatorialOutputs(combRoutes, node, value(inputPortNumberOpt));
        case 1:
            return concat(node.Outputs);
        case 2:
            return empty();
        default:
            return concat(node.Outputs);
    }
}

function dfs(graph, combPaths, currId, inputPortNumber, visited, outputsReached) {
    const exploreChildren = (visited_1_mut, outputsReached_1_mut, children_mut) => {
        exploreChildren:
        while (true) {
            const visited_1 = visited_1_mut, outputsReached_1 = outputsReached_1_mut, children = children_mut;
            if (!isEmpty(children)) {
                const patternInput = dfs(graph, combPaths, head(children)[0], head(children)[1], visited_1, outputsReached_1);
                visited_1_mut = patternInput[0];
                outputsReached_1_mut = patternInput[1];
                children_mut = tail(children);
                continue exploreChildren;
            }
            else {
                return [visited_1, outputsReached_1];
            }
            break;
        }
    };
    const currNode = getNodeOrFail(graph, currId);
    let inputPortNumber_1;
    const matchValue = currNode.Type;
    switch (matchValue.tag) {
        case 26:
        case 42: {
            inputPortNumber_1 = inputPortNumber;
            break;
        }
        default:
            inputPortNumber_1 = undefined;
    }
    if (FSharpSet__Contains(visited, [currId, inputPortNumber_1])) {
        return [visited, outputsReached];
    }
    else {
        const visited_3 = FSharpSet__Add(visited, [currId, inputPortNumber_1]);
        if (currNode.Type.tag === 1) {
            return [visited_3, cons(currNode.Label, outputsReached)];
        }
        else {
            return exploreChildren(visited_3, outputsReached, getCombinatorialOutputs(combPaths, currNode, inputPortNumber_1));
        }
    }
}

function findCombinatorialPaths(customComp, currGraph, combPaths) {
    const labelToString = (_arg) => _arg;
    const labelsToStrings = (labels) => map_1((tuple) => tuple[0], labels);
    const runDfs = (inputs) => {
        if (!isEmpty(inputs)) {
            const input = head(inputs)[1];
            const patternInput = dfs(currGraph, combPaths, input.Id, 0, empty_1({
                Compare: (x_1, y_1) => (compareArrays(x_1, y_1) | 0),
            }), empty());
            const res = runDfs(tail(inputs));
            const outputsPNums = map_1((arg) => (labelToPortNumber(labelToString(arg), labelsToStrings(customComp.OutputLabels)) | 0), patternInput[1]);
            return FSharpMap__Add(res, labelToPortNumber(labelToString(input.Label), labelsToStrings(customComp.InputLabels)), map_1((Item_1) => (Item_1 | 0), outputsPNums));
        }
        else {
            return empty_2({
                Compare: (x, y) => (compare(x, y) | 0),
            });
        }
    };
    return runDfs(toList(filter_1((compId, comp) => isInput(comp.Type), currGraph)));
}

function exploreNestedComponents(currGraph, currName, currStack, result) {
    const currStack_1 = FSharpSet__Add(currStack, currName);
    const iterateNestedComponents = (res_mut, nested_mut) => {
        iterateNestedComponents:
        while (true) {
            const res = res_mut, nested = nested_mut;
            if (!isEmpty(nested)) {
                if (res == null) {
                    return undefined;
                }
                else if (res != null) {
                    const result_1 = value(res);
                    const matchValue = calculateCustomCompCombPaths(head(nested)[0], getCustomName(head(nested)[1]), getCustomComponentType(head(nested)[1]), currStack_1, result_1);
                    if (matchValue != null) {
                        res_mut = matchValue;
                        nested_mut = tail(nested);
                        continue iterateNestedComponents;
                    }
                    else {
                        return undefined;
                    }
                }
                else {
                    return toFail(printf("what? Impossible case in iterateNestedComponents"));
                }
            }
            else {
                return res;
            }
            break;
        }
    };
    return iterateNestedComponents(result, ofSeq(map_2((comp_1) => [value(comp_1.CustomSimulationGraph), comp_1.Type], filter_2((comp) => isCustom(comp.Type), SimGraph_values(currGraph)))));
}

function calculateCustomCompCombPaths(currGraph, currName, customComp, currStack, result) {
    const matchValue = FSharpSet__Contains(currStack, currName);
    const matchValue_1 = FSharpMap__ContainsKey(result, currName);
    if (matchValue) {
        return undefined;
    }
    else if (matchValue_1) {
        return result;
    }
    else {
        const matchValue_3 = exploreNestedComponents(currGraph, currName, currStack, result);
        if (matchValue_3 != null) {
            const result_1 = matchValue_3;
            return FSharpMap__Add(result_1, currName, findCombinatorialPaths(customComp, currGraph, result_1));
        }
        else {
            return undefined;
        }
    }
}

/**
 * For each dependecy in a simulation graph, create a map containing:
 * - key: name of the custom component.
 * - value: a map with:
 * - key: each InputPortNumber
 * - value: a list of OutputPortNumber combinatorially connected to the
 * input.
 * An input is considered combinatorially connected to an output if there is at
 * least one logic path connecting an input directly with the output. In other
 * words, there must be at least one route from the input to output that does
 * not encounter any synchronous component.
 * Return None if such information cannot be inferred, for example if there is
 * a circular dependency.
 */
export function calculateCustomComponentsCombinatorialPaths(diagramName, graph) {
    return exploreNestedComponents(graph, diagramName, empty_1({
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    }), empty_2({
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    }));
}

